import { useEffect, useMemo, useState } from 'react';
import type { CreateCookLogRequest, GetRecipeResponse as RecipeDetailData, Rating } from '@mlc/shared';
import { createCookLog, OfflineQueuedError, uploadCookLogPhoto } from './api';
import { Link } from './router';
import { Banner, Button, buttonClasses, fieldClasses } from './ui';

export interface CookLogFormProps {
  recipeId: string;
  /** Durée mesurée depuis le début du mode cuisine, ou `null` hors mode cuisine (ajout a posteriori). */
  prefilledActualMinutes: number | null;
  /** « Passer » dans l'écran de fin du mode cuisine, « Annuler » depuis la fiche recette. */
  dismissLabel: string;
  /**
   * Adresse de l'écran d'édition, ouvert sur les étapes (US-74) — proposée
   * **seulement** quand la recette qu'on vient de cuisiner n'a pas d'étapes et
   * que son procédé est ailleurs. `null` partout ailleurs, et c'est le cas de
   * la quasi-totalité de la bibliothèque.
   */
  lienEtapes?: string | null;
  onSaved: (detail: RecipeDetailData) => void;
  onDismiss: () => void;
}

const RATINGS: Rating[] = [1, 2, 3, 4, 5];

function toNullableNumber(value: string): number | null {
  const trimmed = value.trim();
  return trimmed.length === 0 ? null : Number(trimmed);
}

function toNullableText(value: string): string | null {
  const trimmed = value.trim();
  return trimmed.length === 0 ? null : trimmed;
}

// Styles partagés (B1, refonte UX/UI passe 3) : voir ui/Field.tsx et ui/Button.tsx.
const fieldClass = fieldClasses('default');

/**
 * Formulaire de réalisation (US-10), réutilisé dans deux contextes : l'écran
 * de fin automatique du mode cuisine (`CookMode.tsx`, `actual_minutes`
 * préremplie depuis la durée mesurée) et l'ajout a posteriori depuis la fiche
 * recette (`RecipeDetail.tsx`, sans préremplissage — AC US-10 : « la
 * réalisation reste ajoutable a posteriori »).
 *
 * Soumis en un seul geste (« Enregistrer »), contrairement au reste de la
 * fiche recette qui enregistre chaque champ sur place au blur (US-06) :
 * un enregistrement au blur casserait « l'écran est passable » (AC US-10) —
 * une réalisation partiellement saisie se retrouverait déjà en base avant
 * même que « Passer »/« Annuler » soit cliqué. Voir DECISIONS.md « US-10 ».
 */
export function CookLogForm({
  recipeId,
  prefilledActualMinutes,
  dismissLabel,
  lienEtapes = null,
  onSaved,
  onDismiss,
}: CookLogFormProps): React.JSX.Element {
  const [rating, setRating] = useState<Rating | null>(null);
  const [servingsMade, setServingsMade] = useState('');
  const [actualMinutes, setActualMinutes] = useState(
    prefilledActualMinutes !== null ? String(prefilledActualMinutes) : '',
  );
  const [whatHappened, setWhatHappened] = useState('');
  const [nextTime, setNextTime] = useState('');
  const [photoFile, setPhotoFile] = useState<File | null>(null);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Aperçu local uniquement : la photo n'est envoyée qu'à l'enregistrement
  // (cf. commentaire de tête — pas d'upload immédiat au choix du fichier,
  // contrairement à PhotoField dans RecipeDetail.tsx, US-06).
  const previewUrl = useMemo(() => (photoFile !== null ? URL.createObjectURL(photoFile) : null), [photoFile]);
  useEffect(() => () => { if (previewUrl !== null) URL.revokeObjectURL(previewUrl); }, [previewUrl]);

  function handlePhotoChange(event: React.ChangeEvent<HTMLInputElement>): void {
    const file = event.target.files?.[0];
    event.target.value = '';
    if (file) setPhotoFile(file);
  }

  async function handleSave(): Promise<void> {
    setSaving(true);
    setError(null);
    try {
      const body: CreateCookLogRequest = {
        servings_made: toNullableNumber(servingsMade),
        rating,
        actual_minutes: toNullableNumber(actualMinutes),
        what_happened: toNullableText(whatHappened),
        next_time: toNullableText(nextTime),
      };
      const created = await createCookLog(recipeId, body);
      const logId = created.cook_logs[0]?.id;
      if (photoFile !== null && logId !== undefined) {
        onSaved(await uploadCookLogPhoto(recipeId, logId, photoFile));
      } else {
        onSaved(created);
      }
    } catch (e) {
      if (e instanceof OfflineQueuedError) {
        // Pas de fiche à jour à renvoyer à `onSaved` (rien n'a atteint le
        // serveur) : on referme l'écran comme un enregistrement réussi
        // plutôt que d'exiger un nouveau geste — le compteur d'écritures en
        // attente (App.tsx) reste visible depuis les autres écrans. La photo
        // choisie, elle, n'est pas mise en file (cf. offlineQueue.ts) et
        // reste à ré-ajouter après la reconnexion via « Ajouter une réalisation ».
        setSaving(false);
        onDismiss();
        return;
      }
      setError(e instanceof Error ? e.message : 'Erreur inconnue.');
      setSaving(false);
    }
  }

  return (
    <div aria-label="Réalisation" className="flex flex-col gap-4">
      {error !== null && <Banner tone="danger">{error}</Banner>}

      {/* **Noter les étapes pendant que c'est frais** (US-74).
          Une recette dont le procédé est resté dans la vidéo vient d'être
          faite : c'est le seul moment où quelqu'un sait vraiment comment on
          s'y prend, et l'occasion ne se représente pas.

          **En tête du formulaire, et c'est délibéré** : le lien quitte cet
          écran, donc il doit se lire avant qu'on ait commencé à écrire, pas
          après. Placé sous « La prochaine fois », il aurait fait perdre ce
          qu'on venait de taper — une invitation qui coûte le travail de
          quelqu'un n'est pas une invitation.

          Facultatif, comme tout le reste de cet écran : on le passe en ne le
          regardant pas. */}
      {lienEtapes !== null && (
        <p className="flex flex-wrap items-center gap-x-2 gap-y-1 text-sm text-foreground-secondary">
          Le procédé n’est pas dans la fiche.
          <Link to={lienEtapes} className={buttonClasses('ghost', 'compact')}>
            Noter les étapes pendant que c’est frais
          </Link>
        </p>
      )}

      <fieldset className="flex flex-col gap-1">
        <legend className="text-sm font-medium text-foreground">Note</legend>
        <div className="flex gap-2">
          {RATINGS.map((value) => (
            <button
              key={value}
              type="button"
              aria-pressed={rating === value}
              aria-label={`Note ${value} sur 5`}
              onClick={() => { setRating(rating === value ? null : value); }}
              className={`flex min-h-touch min-w-touch items-center justify-center rounded-control border text-lg transition-colors ${
                rating === value
                  ? 'border-primary bg-primary text-primary-foreground'
                  : 'border-border text-foreground'
              }`}
            >
              {value}
            </button>
          ))}
        </div>
      </fieldset>

      <label className="flex flex-col gap-1 text-sm">
        Ce qui s’est passé
        <textarea
          aria-label="Ce qui s’est passé"
          className={`${fieldClass} min-h-24`}
          value={whatHappened}
          onChange={(e) => { setWhatHappened(e.target.value); }}
        />
      </label>

      <label className="flex flex-col gap-1 text-sm">
        La prochaine fois
        <textarea
          aria-label="La prochaine fois"
          className={`${fieldClass} min-h-24`}
          value={nextTime}
          onChange={(e) => { setNextTime(e.target.value); }}
        />
      </label>

      <div className="grid grid-cols-2 gap-4">
        <label className="flex flex-col gap-1 text-sm">
          Temps réel (min)
          <input
            aria-label="Temps réel (min)"
            type="number"
            inputMode="numeric"
            className={`${fieldClass} text-data`}
            value={actualMinutes}
            onChange={(e) => { setActualMinutes(e.target.value); }}
          />
        </label>
        <label className="flex flex-col gap-1 text-sm">
          Portions obtenues
          <input
            aria-label="Portions obtenues"
            type="number"
            inputMode="decimal"
            className={`${fieldClass} text-data`}
            value={servingsMade}
            onChange={(e) => { setServingsMade(e.target.value); }}
          />
        </label>
      </div>

      <div className="flex flex-col gap-2">
        <label
          htmlFor="cook-log-photo-input"
          className="flex aspect-video w-full max-w-xs cursor-pointer items-center justify-center overflow-hidden rounded-card border border-dashed border-border bg-surface-raised text-sm"
        >
          {previewUrl !== null ? (
            <img src={previewUrl} alt="" className="h-full w-full animate-fade-in object-cover" />
          ) : (
            <span className="p-4 text-center text-foreground-muted">Ajouter une photo</span>
          )}
        </label>
        {/* « en une seule action depuis l'appareil photo » (AC US-10) :
            `capture` ouvre directement l'appareil photo sur mobile plutôt
            qu'un sélecteur de galerie — PhotoField (US-06) n'a pas cette
            exigence explicite et ne l'utilise pas. */}
        <input
          id="cook-log-photo-input"
          aria-label="Photo de la réalisation"
          type="file"
          accept="image/jpeg,image/png,image/webp"
          capture="environment"
          className="sr-only"
          onChange={handlePhotoChange}
        />
      </div>

      <div className="flex items-center justify-between gap-4 pt-2">
        <Button type="button" variant="secondary" onClick={onDismiss} disabled={saving}>
          {dismissLabel}
        </Button>
        {/* §4 : « le libellé d'un contrôle ne change pas pendant l'action ».
            Le bouton disait « Enregistrement… » à la place de « Enregistrer »,
            ce qui fait perdre de vue ce qu'on avait demandé ; `aria-busy` et
            `disabled` portent l'état, le libellé reste. */}
        <Button type="button" variant="primary" onClick={() => { void handleSave(); }} disabled={saving} aria-busy={saving}>
          Enregistrer
        </Button>
      </div>
    </div>
  );
}
